import "@xyflow/react/dist/style.css";
import { useMemo } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { Background, Controls, MiniMap, ReactFlow, type Edge, type Node } from "@xyflow/react";
import Box from "@mui/material/Box";
import { LoadingState, ErrorState, EmptyState } from "@/components/feedback/states";
import { useFlowColorMode } from "@/components/pipeline-canvas/use-flow-color-mode";
import { type LineageGraph, useLineageGraph } from "@/api/catalog";

export const Route = createFileRoute("/_app/catalog/lineage")({
  component: LineageTab,
});

const TYPE_COLOR: Record<string, string> = {
  CONNECTOR: "#4f46e5",
  PIPELINE: "#7c3aed",
  EXECUTION: "#0ea5e9",
};

function LineageTab() {
  const { data, isLoading, error, refetch } = useLineageGraph();
  const colorMode = useFlowColorMode();

  const { nodes, edges } = useMemo(() => toFlow(data), [data]);

  if (isLoading) return <LoadingState />;
  if (error) return <ErrorState error={error} onRetry={() => refetch()} />;
  if (nodes.length === 0)
    return (
      <EmptyState
        title="No lineage yet"
        description="Lineage is captured as pipelines run — trigger some runs and the graph fills in."
      />
    );

  return (
    <Box sx={{ height: "70vh", border: 1, borderColor: "divider", borderRadius: 2 }}>
      <ReactFlow
        nodes={nodes}
        edges={edges}
        colorMode={colorMode}
        fitView
        nodesConnectable={false}
        proOptions={{ hideAttribution: true }}
      >
        <Background gap={16} />
        <Controls showInteractive={false} />
        <MiniMap pannable />
      </ReactFlow>
    </Box>
  );
}

// Nodes of the same type stack in a column; wrap into a second sub-column
// once a column gets tall so a lopsided graph (e.g. all executions) stays readable.
const COL_X: Record<string, number> = { CONNECTOR: 0, PIPELINE: 340, EXECUTION: 680 };
const ROWS_PER_COL = 8;
const ROW_H = 80;
const SUBCOL_W = 150;

function toFlow(g: LineageGraph | undefined): { nodes: Node[]; edges: Edge[] } {
  if (!g) return { nodes: [], edges: [] };
  const seen: Record<string, number> = {};
  const nodes: Node[] = g.nodes.map((n) => {
    const type = (n.type ?? "NODE").toUpperCase();
    const idx = seen[type] ?? 0;
    seen[type] = idx + 1;
    const baseX = COL_X[type] ?? 1020;
    const row = idx % ROWS_PER_COL;
    const sub = Math.floor(idx / ROWS_PER_COL);
    return {
      id: n.id,
      position: { x: baseX + sub * SUBCOL_W, y: row * ROW_H },
      data: { label: n.label ?? n.id },
      style: {
        border: `2px solid ${TYPE_COLOR[type] ?? "#64748b"}`,
        borderRadius: 8,
        padding: 8,
        fontSize: 12,
        maxWidth: 200,
        background: "var(--mui-palette-background-paper)",
        color: "var(--mui-palette-text-primary)",
      },
    };
  });
  const edges: Edge[] = g.edges.map((e, i) => ({
    id: `e${i}`,
    source: e.source ?? e.from ?? "",
    target: e.target ?? e.to ?? "",
    animated: true,
    label: e.type ?? undefined,
  }));
  return { nodes, edges };
}
